<template>
  <div class="recover-password-container">
    <section>
      <b-button
        class="btn-back-login"
        title="Volver al inicio de sesión"
        variant="light"
        @click="redirectToLogin">
        <i class="fad fa-home-lg-alt"></i>
      </b-button>
      <img class="bm-logo" src="@/assets/img/bm-logo-color.png" />
      <h1>{{ recoveryTitle }}</h1>
      <p v-if="isExpirationPwd || isFirstLogin" class="alert alert-primary">
        <i class="fad fa-info-circle mr-1"></i>Por motivos de seguridad, se requiere que la contraseña sea cambiada.
      </p>
      <b-tabs fill v-model="currentStep" small active-nav-item-class="font-weight-bold text-primary">
        <b-tab title="1. Credenciales">
          <b-card>
            <p class="mb-2">Ingresa tu dominio y usuario</p>
            <form @submit.prevent="getRecoveryPasswordMedias">
              <div class="mb-2">
                <label for="domain" class="mb-0">Dominio</label>
                <b-input-group size="sm">
                  <b-input-group-prepend is-text class="input-icon">
                    <i class="fa fa-slash flip-v"></i>
                  </b-input-group-prepend>
                  <b-form-input
                    id="domain"
                    placeholder="Dominio"
                    v-model.trim="domain"
                    autofocus
                    @change="setFeedbackValuesMessage('')"></b-form-input>
                </b-input-group>
              </div>

              <div>
                <label for="username" class="mb-0">Usuario</label>
                <b-input-group size="sm">
                  <b-input-group-prepend is-text class="input-icon">
                    <i class="fa fa-user-circle"></i>
                  </b-input-group-prepend>
                  <b-form-input
                    id="username"
                    placeholder="Usuario"
                    v-model.trim="username"
                    @change="setFeedbackValuesMessage('')"></b-form-input>
                </b-input-group>
              </div>
              <b-button :disabled="!isDomainAndUsername"
                type="submit"
                class="mt-2"
                variant="primary"
                size="sm"
                block>
                {{ !validatingCredentials ? 'Enviar' : ''}} <b-spinner small v-if="validatingCredentials"></b-spinner>
              </b-button>
              <b-alert
                class="mt-2 mb-0"
                variant="danger"
                dismissible
                :show="feedbackValues !== ''">
                {{ feedbackValues }}
              </b-alert>
            </form>
          </b-card>
        </b-tab>
        <b-tab :title="`2. ${recoveryMediaTitle}`"
          :disabled="!existsRecoveryMedia || isMobile" lazy>
          <b-card>
            <div class="mb-2">
              <p class="m-0 small">Dominio: <span class="font-weight-bold">{{ domain }}</span></p>
              <p class="m-0 small">Usuario: <span class="font-weight-bold">{{ username }}</span></p>
            </div>
            <b-form @submit.prevent="requestRecoveryPasswordPin">
              <b-form-group label="Selecciona un medio para verificar tu identidad" label-size="sm" >
                <b-form-radio-group
                  v-model="media"
                  :options="recoveryMediaOptionsForRadioGroup"
                  stacked
                  name="radio-options"
                ></b-form-radio-group>
                <small>A través del medio seleccionado se enviará el PIN de verificación con el cual será posible actualizar la contraseña. Asegurate de tener acceso a dicho medio.</small>
              </b-form-group>
              <b-button :disabled="!media || PINCounterDown > 0"
                type="submit"
                class="request-pin-button"
                variant="primary"
                size="sm"
                block>
                <p v-if="PINCounterDown === 0">Solicitar PIN</p>
                <p v-else>Tiempo de espera para solicitar nuevo PIN ... <i class="far fa-stopwatch"></i> {{PINCounterDown}} seg</p>
              </b-button>
            </b-form>
            <b-alert class="mt-2 mb-0" variant="danger" :show="feedbackPIN !== '' && !sendingRecoveryPin">
              {{feedbackPIN}}
            </b-alert>
          </b-card>
        </b-tab>
        <b-tab class="update-password-tab" title="3. Actualización" :disabled="!isRecoveryPINReceived" lazy>
          <b-card>
            <div class="mb-2">
              <p class="m-0 small">Dominio: <span class="font-weight-bold">{{ domain }}</span></p>
              <p class="m-0 small">Usuario: <span class="font-weight-bold">{{ username }}</span></p>
            </div>
            <b-form @submit.prevent="updatePassword">
              <p :show="recoveryPinSent && !sendingRecoveryPin" class="mt-2 mb-0">
                Te hemos enviado un PIN de verificación a <span class="font-weight-bold">{{ recoveryMediaOptions[media] }}</span>. Este tiene una duración de 10 minutos.
                Por favor, ingresa el PIN recibido junto con tu nueva contraseña para actualizarla.
              </p>
              <b-button size="sm" block @click="requestRecoveryPasswordPin" variant="info" :disabled="!media || PINCounterDown > 0">
                <p v-if="PINCounterDown === 0" class="m-0">Solicitar PIN</p>
                <p v-else class="m-0">Tiempo de espera para solicitar nuevo PIN ... <i class="far fa-stopwatch"></i> {{PINCounterDown}} seg</p>
              </b-button>
              <b-input-group class="mt-2"
                size="sm">
                <b-input-group-prepend
                  id="pin-icon"
                  is-text
                  class="input-icon">
                  <i class="fas fa-key-skeleton"></i>
                </b-input-group-prepend>
                <b-form-input
                  name="pin"
                  :disabled="passwordUpdated"
                  placeholder="PIN de verificación. Ej: B-000001"
                  type="text"
                  v-model.trim="recoveryPin"
                  autofocus/>
              </b-input-group>
              <b-tooltip target="pin-icon">PIN de verficación.</b-tooltip>
              <b-input-group class="mt-2"
                size="sm">
                <b-input-group-prepend is-text class="input-icon">
                  <i class="fa fa-key"></i>
                </b-input-group-prepend>
                <b-form-input
                  name="new-password"
                  :disabled="passwordUpdated"
                  placeholder="Nueva contraseña"
                  :type="passwordHide"
                  v-model.trim="newPassword"
                  lazy
                  :state="validatePasswordRequirements()"
                  @change="setFeedbackPasswordMessage('')"/>
                  <span class="show-password-icon"
                    v-if="!passwordUpdated"
                  @click="changeShowPassword(setShowPassword, getShowPassword)">
                  <i v-if="!showPassword" class="fas fa-eye"></i>
                  <i v-else class="fas fa-eye-slash"></i>
                </span>
                <b-form-invalid-feedback id="password-not-match-requirements">
                  Verifica que la contraseña ingresada cumple las especificaciones
                </b-form-invalid-feedback>
              </b-input-group>
              <b-input-group class="mt-2 mb-2"
                size="sm">
                <b-input-group-prepend is-text class="input-icon">
                  <i class="fa fa-key"></i>
                </b-input-group-prepend>
                <b-form-input
                  :disabled="passwordUpdated"
                  placeholder="Confirmar nueva contraseña"
                  :type="passwordConfirmationHide"
                  v-model.trim="newPasswordConfirmation"
                  lazy
                  :state="validatePaswwordConfirmation()"
                  aria-describedby="password-not-match-confirmation"
                  @change="setFeedbackPasswordMessage('')"/>
                  <span class="show-password-icon"
                    v-if="!passwordUpdated"
                    @click="changeShowPassword(setShowPasswordConfirmation, getShowPasswordConfirmation)">
                    <i v-if="!showPasswordConfirmation" class="fas fa-eye"></i>
                    <i v-else class="fas fa-eye-slash"></i>
                  </span>
                <b-form-invalid-feedback id="password-not-match-confirmation">
                  Las contraseñas no coinciden, por favor verifica sus valores.
                </b-form-invalid-feedback>
              </b-input-group>
              <password-details description="Tu contraseña debe cumplir lo siguiente:" :domain-security-level="domainSecurityLevel"></password-details>
              <b-button
                v-if="!passwordUpdated"
                class="mt-2"
                type="submit"
                :disabled="!existPinAndPassword || !validatePaswwordConfirmation()"
                variant="primary"
                size="sm"
                block>
                {{ !updatingPassword ? 'Actualizar contraseña' : ''}} <b-spinner small v-if="updatingPassword"></b-spinner>
              </b-button>
              <b-alert class="mt-2 mb-0" dismissible :show="feedbackPassword !== ''" variant="danger">
                {{ feedbackPassword }}
              </b-alert>
            </b-form>
            <b-alert
              class="mt-2 mb-0"
              :show="passwordUpdated || loginCounterDown > 0"
              variant="success">
              <strong>Contraseña actualizada</strong>
              <p>Tu contraseña ha sido actualizada correctamente. Vuelve al inicio de sesión para ingresar al sitio.</p>
              <small>Redireccionamiento al inicio de sesión en <i class="far fa-stopwatch"></i> {{ loginCounterDown }} seg </small>
            </b-alert>
          </b-card>
        </b-tab>
      </b-tabs>
    </section>
  </div>
</template>

<script lang="ts">
import { APIErrorFormat } from '@/types/Errors'
import Vue from 'vue'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { PASSWORD_REGEXP } from '@/types/RegExpressions'
import { RecoveryReasons } from '@/types/Login'
import router from '@/router'
import LogService from '@/services/support/LogService'
import { DomainSecurityLevel } from '@/types/Catalogs'
import RecoveryPasswordActions from '@/services/RecoveryPassword'
import { RecoveryPasswordDetails } from '@/types/RecoveryPassword'
import PasswordDetails from '@/components/generic/PasswordDetails.vue'

@Component({
  name: 'RecoverPassword',
  components: {
    PasswordDetails
  }
})
export default class RecoverPassword extends Vue {
  domain: string = ''
  username: string = ''
  newPassword: string = ''
  newPasswordConfirmation: string = ''
  recoveryPin: string = ''
  recoveryMediaOptions: { [key: string]: string | number } = {}
  media: string = ''
  recoveryPinSent: boolean = false
  recoveryPINReceived: boolean = false
  passwordUpdated: boolean = false
  feedbackValues: string = ''
  feedbackPIN: string = ''
  feedbackPassword: string = ''
  currentStep: number = 0

  validatingCredentials: boolean = false
  sendingRecoveryPin: boolean = false
  updatingPassword: boolean = false

  PINCounterDown: number = 0
  PINCounter: number = 60
  showPassword: boolean = false
  showPasswordConfirmation: boolean = false
  loginCounterDown: number = 0
  loginCounter: number = 10
  domainSecurityLevel: number = DomainSecurityLevel.HIGH
  isMobile: boolean = false

  recoveryValidMedias: string[] = [
    'email',
    'emailExtra',
    'phone',
    'phoneExtra'
  ]

  /** Validates if the domain and username were filled */
  get isDomainAndUsername () : boolean {
    return this.domain.length > 0 && this.username.length > 0
  }

  /** Validates if the domain, username, PIN and password were filled */
  get existPinAndPassword () : boolean {
    return this.isDomainAndUsername &&
      this.recoveryPin.length > 0 &&
      this.newPassword.length > 0
  }

  get routeRecoveryReasonParam (): RecoveryReasons | undefined {
    return this.$route.params.recoveryReason as RecoveryReasons | undefined
  }

  get isExpirationPwd () {
    return this.routeRecoveryReasonParam === 'expirationPwd'
  }
  get isFirstLogin () {
    return this.routeRecoveryReasonParam === 'fisrtLogin'
  }
  get isRecoveryPwd () {
    return this.routeRecoveryReasonParam === 'recoveryPassword'
  }

  get recoveryTitle () {
    if (this.isExpirationPwd || this.isFirstLogin) {
      return 'Cambio de contraseña'
    }
    return 'Recuperación de contraseña'
  }

  get recoveryMediaTitle () {
    if (this.isExpirationPwd || this.isFirstLogin) {
      return 'Medios de actualización'
    }
    return 'Medios de recuperación'
  }

  get recoveryMediaOptionsForRadioGroup () {
    return Object.entries(this.recoveryMediaOptions)
      .map(([value, text]) => (
        { value, text }
      ))
  }

  get recoveryPasswordDetails () {
    return RecoveryPasswordDetails[this.domainSecurityLevel]
  }

  /**
   * Gets the recovery medias by domain and username.
   */
  async getRecoveryPasswordMedias () {
    this.validatingCredentials = true
    this.recoveryMediaOptions = {}
    try {
      const recoveryPasswordMedias = await RecoveryPasswordActions.getRecoveryPasswordMedias(this.domain, this.username)
      const { domainSecurityLevel, isMobile, ...recoveryMediaOptions } = recoveryPasswordMedias
      this.domainSecurityLevel = domainSecurityLevel
      this.recoveryMediaOptions = recoveryMediaOptions
      this.isMobile = isMobile
      if (isMobile) {
        this.setFeedbackValuesMessage(this.$t('recoveryPassword.errors.mobileUser').toString())
        return
      }
      setTimeout(() => {
        this.setCurrentStep(1)
      }, 0)
    } catch (error: any) {
      this.manageFeedbackError(APIErrorFormat.getInstance(error.response.data))
    } finally {
      this.validatingCredentials = false
    }
  }

  /**
   * Process the request to get a PIN to update the password.
   */
  async requestRecoveryPasswordPin () {
    this.sendingRecoveryPin = true
    try {
      await RecoveryPasswordActions.requestRecoveryPasswordPin(this.domain, this.username, this.transformMediaRecovery(this.media))
      this.recoveryPinSent = true
      this.counterStart(this.PINCounter, this.setPINCounter)
      this.recoveryPINReceived = true
      setTimeout(() => {
        this.setCurrentStep(2)
      }, 0)
    } catch (error: any) {
      this.manageFeedbackError(APIErrorFormat.getInstance(error.response.data))
    } finally {
      this.sendingRecoveryPin = false
    }
  }

  /**
   * Timer for manages ranges of N seconds
   *
   * @param counter N seconds for timer
   * @param setCounter function that updates the counter value
   */
  counterStart (counter: number, setCounter: any) {
    setCounter(counter)
    setTimeout(() => {
      if (counter > 0) {
        this.counterStart(counter - 1, setCounter)
      }
    }, 1000)
  }

  /**
   * Sets the PINCounterDown variable
   *
   * @param value to set
   */
  setPINCounter (value: number) {
    this.PINCounterDown = value
  }

  /**
   * Sets the LoginCounterDown variable
   *
   * @param value to set
   */
  setLoginCounter (value: number) {
    this.loginCounterDown = value
  }

  /**
   * Validates the requirements for the new password
   */
  validatePasswordRequirements () {
    if (this.newPassword.length > 0) {
      if (this.domainSecurityLevel === DomainSecurityLevel.LOW) {
        return new RegExp(PASSWORD_REGEXP.low_password).test(this.newPassword)
      } else if (this.domainSecurityLevel === DomainSecurityLevel.MEDIUM) {
        return new RegExp(PASSWORD_REGEXP.medium_password).test(this.newPassword)
      } else {
        return new RegExp(PASSWORD_REGEXP.high_password).test(this.newPassword)
      }
    }
    return null
  }

  /**
   * Validates that the new password and its confirmation are the same
   */
  validatePaswwordConfirmation () {
    return this.newPassword.length && this.newPasswordConfirmation.length
      ? this.newPassword === this.newPasswordConfirmation
      : null
  }

  /**
   * To change the input type for the new password
   */
  get passwordHide () {
    return this.showPassword ? 'text' : 'password'
  }

  /**
   * To change the input type for the confirmation password
   */
  get passwordConfirmationHide () {
    return this.showPasswordConfirmation ? 'text' : 'password'
  }

  /**
   * Changes the value for the input password type
   *
   * @param updateValue function to set the flag value
   * @param currentValue function to get the current value flag
   */
  changeShowPassword (updateValue: any, currentValue: any) {
    updateValue(!currentValue())
  }

  /**
   * Sets the showPassword value
   *
   * @param value to set
   */
  setShowPassword (value: boolean) {
    this.showPassword = value
  }

  /**
   * @returns the showPassword value
   */
  getShowPassword () {
    return this.showPassword
  }
  /**
   * Sets the showPasswordConfirmation value
   *
   * @param value to set
   */
  setShowPasswordConfirmation (value: boolean) {
    this.showPasswordConfirmation = value
  }
  /**
   * @returns the showPasswordConfirmation value
   */
  getShowPasswordConfirmation () {
    return this.showPasswordConfirmation
  }
  /**
   * Updates the new password for the user.
   */
  async updatePassword () {
    this.updatingPassword = true
    try {
      const params = {
        domain: this.domain,
        user: this.username,
        pin: this.recoveryPin,
        newPassword: this.newPassword
      }
      await RecoveryPasswordActions.updatePassword(params)
      this.passwordUpdated = true
      this.loginCounterStart()
      this.reditectToLoginContinue()
    } catch (error: any) {
      this.manageFeedbackError(APIErrorFormat.getInstance(error.response.data))
    } finally {
      this.updatingPassword = false
    }
  }

  loginCounterStart () {
    this.counterStart(this.loginCounter, this.setLoginCounter)
  }

  reditectToLoginContinue () {
    setTimeout(() => {
      this.redirectToLogin()
    }, this.loginCounter * 1000)
  }

  /**
   * Manages the error recovery password massages and translate them into feedback
   *
   * @param error response from api
   */
  manageFeedbackError (error: APIErrorFormat) {
    let errorCode
    let originalErrorDescription = 'Error'
    if (error.isNewErrorFormat()) {
      errorCode = error.errors[0].code
      originalErrorDescription = error.errors[0].description
    }
    if (errorCode !== undefined) {
      switch (errorCode) {
        case 3240:
          this.setFeedbackValuesMessage(originalErrorDescription)
          break
        case 3242:
        case 3230:
          this.setFeedbackPINMessage(originalErrorDescription)
          break
        case 2013:
        case 2014:
        case 2016:
          this.setFeedbackPasswordMessage(originalErrorDescription)
          break
      }
    }
  }

  /** Sets the value for the credentials feedback. */
  setFeedbackValuesMessage (msg: string) {
    this.feedbackValues = msg
  }

  /** Sets the value for request PIN feedback. */
  setFeedbackPINMessage (msg: string) {
    this.feedbackPIN = msg
  }

  /** Sets the value for the update password feedback. */
  setFeedbackPasswordMessage (msg: string) {
    this.feedbackPassword = msg
  }

  /**
   * Changes the current active tab
   * @param index of the tab to change
   */
  setCurrentStep (index: number) {
    this.currentStep = index
  }

  /** Changes to the last tab to update the password. */
  pinReceivedContinue () {
    this.recoveryPINReceived = true
    setTimeout(() => {
      this.setCurrentStep(2)
    }, 300)
  }
  /**
   * Translate the recovery media
   *
   * @param media to translate
   */
  mediaTranslate (media: string) {
    let mediaTranslate = ''
    switch (media) {
      case 'email':
        mediaTranslate = 'Correo'
        break
      case 'phone':
        mediaTranslate = 'Teléfono'
        break
      case 'emailExtra':
        mediaTranslate = 'Correo extra'
        break
      case 'phoneExtra':
        mediaTranslate = 'Teléfono extra'
        break
      default:
        mediaTranslate = 'Medio no reconocido'
        break
    }
    return mediaTranslate
  }
  /**
   * Translate the recovery media value for send it in the pin request
   *
   * @param media recovery to transform
   */
  transformMediaRecovery (media: string) {
    let mediaTranslate = ''
    switch (media) {
      case 'email':
        mediaTranslate = 'email'
        break
      case 'phone':
        mediaTranslate = 'phone'
        break
      case 'emailExtra':
        mediaTranslate = 'email_extra'
        break
      case 'phoneExtra':
        mediaTranslate = 'phone_extra'
        break
      default:
        mediaTranslate = 'none'
        break
    }
    return mediaTranslate
  }
  /**
   * Watchs the domain value change and clears all the current values
   * to update the password
   */
  @Watch('domain')
  onChangeDomain () {
    this.clearAllRecovery()
  }

  /**
   * Watchs the username value change and clears all the current values
   * to update the password
   */
  @Watch('username')
  onChangeUser () {
    this.clearAllRecovery()
  }

  /** @returns if there are some recovery media. */
  get existsRecoveryMedia () {
    return Object.keys(this.recoveryMediaOptions).length > 0
  }
  /** @returns the value of the recoveryPINReceived */
  get isRecoveryPINReceived () {
    return this.recoveryPINReceived
  }
  /** Clears all the current values used to update the user password. */
  clearAllRecovery () {
    this.newPassword = ''
    this.recoveryPin = ''
    this.recoveryMediaOptions = {}
    this.media = ''
    this.recoveryPinSent = false
    this.recoveryPINReceived = false
    this.passwordUpdated = false
    this.feedbackValues = ''
    this.feedbackPIN = ''
    this.feedbackPassword = ''
    this.setCurrentStep(0)
  }
  /** Redirecto to the login view */
  redirectToLogin () {
    if (this.$route.name !== 'login') {
      router.push({ name: 'login' })
        .catch((error) => {
          LogService.error('Error on redirect to login. ' + error)
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.recover-password-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow: auto;
}
.recover-password-container > section {
  position: relative;
  width: 60%;
  min-width: 320px;
  max-width: 600px;
  height: fit-content;
  min-height: 250px;
  max-height: auto;
  padding: 16px;
  border: 0;
  border-radius: 2px;
  box-shadow: 0 0 8px 0 #e9ecef;
}
.recover-password-container > section > img {
  display: block;
  width: 45%;
  margin: auto;
  min-width: 280px;
}
.recover-password-container > section > h1 {
  font-size: 20px;
  margin: 12px 0;
  text-align: center;
}
.btn-back-login {
  position: absolute;
  top: 0px;
  left: 0px;
  padding: 16px;
}
.input-icon {
  width: 30px;
}
.alert.pin-counter {
  height: 2rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  text-align: center;
}
.show-password-icon {
  position: absolute;
  top: 0;
  right: 0;
  margin: 0.25rem 0.5rem;
}
.update-password-tab > .was-validated .form-control:invalid, .form-control.is-invalid {
  background-image: none;
}
.update-password-tab > .was-validated .form-control:invalid, .form-control.is-valid {
  background-image: none;
}
.request-pin-button > p {
  margin: 0;
}
</style>
